'use client';

import { FC, useCallback } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Delete as DeleteIcon, ArrowUpward as ArrowUpIcon } from '@mui/icons-material';
import type { FileMetaDataConfigDto, FileMetaDataFieldConfigDto } from '@/api/ApiClient';
import { useSelectOptions } from '@/lib/options/useSelectOptions';
import { OptionKey } from '@/lib/options/optionTypes';
import {
  INSTRUMENT_METADATA_SOURCE_MANUAL,
  sortMetadataConfigFields,
} from '@/lib/fileMetadata/instrumentMetadataConfig';
import { InstrumentFileMetadataReadonlyPanel } from '@/components/fileMetadata/InstrumentFileMetadataReadonlyPanel';

const OPTION_KEYS: OptionKey[] = ['columnType'];

export interface InstrumentFileMetadataConfigEditorProps {
  config: FileMetaDataConfigDto;
  onChange: (config: FileMetaDataConfigDto) => void;
  /** When false, show read-only field list (no ConfigureFileMetaData privilege). */
  canEdit?: boolean;
}

/**
 * System-wide instrument file metadata (manual fields only).
 * Filename source / pattern / "Value from Test" are omitted until that workflow is ported from Knockout.
 */
export const InstrumentFileMetadataConfigEditor: FC<InstrumentFileMetadataConfigEditorProps> = ({
  config,
  onChange,
  canEdit = true,
}) => {
  const { options, loading: optionsLoading } = useSelectOptions(OPTION_KEYS);

  const fields = sortMetadataConfigFields(config.fields ?? []);

  const emitChange = useCallback(
    (patch: Partial<FileMetaDataConfigDto>, nextFields?: FileMetaDataFieldConfigDto[]) => {
      onChange({
        ...config,
        metadataSource: INSTRUMENT_METADATA_SOURCE_MANUAL,
        ...patch,
        fields: nextFields ?? config.fields,
      });
    },
    [config, onChange]
  );

  const handleFieldChange = useCallback(
    (index: number, patch: Partial<FileMetaDataFieldConfigDto>) => {
      const next = [...fields];
      next[index] = { ...next[index], ...patch };
      emitChange({}, next);
    },
    [emitChange, fields]
  );

  const handleAddField = useCallback(() => {
    emitChange({}, [
      ...fields,
      {
        label: '',
        type: '',
        order: fields.length,
      },
    ]);
  }, [emitChange, fields]);

  const handleDeleteField = useCallback(
    (index: number) => {
      emitChange(
        {},
        fields.filter((_, i) => i !== index)
      );
    },
    [emitChange, fields]
  );

  const handleMoveUp = useCallback(
    (index: number) => {
      if (index < 1) return;
      const next = [...fields];
      [next[index - 1], next[index]] = [next[index], next[index - 1]];
      emitChange({}, next);
    },
    [emitChange, fields]
  );

  if (optionsLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (!canEdit) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Typography variant="subtitle2" color="text.secondary">
          System-wide file metadata for this instrument.
        </Typography>
        <InstrumentFileMetadataReadonlyPanel config={config} />
        {fields.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No file metadata fields configured.
          </Typography>
        ) : null}
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="subtitle2" color="text.secondary">
        System-wide file metadata for this instrument - characterization data can be configured with these fields.
      </Typography>

      {/* Filename metadata source disabled until filename parsing UI is ported.
      <FormControl sx={{ minWidth: 220 }} size="small">
        <InputLabel>Metadata Source</InputLabel>
        <Select value={config.metadataSource || ''} ... />
      </FormControl>
      */}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width={50} />
              <TableCell>Label</TableCell>
              <TableCell>Type</TableCell>
              <TableCell width={50} />
            </TableRow>
          </TableHead>
          <TableBody>
            {fields.map((field, index) => (
              <TableRow key={field.id ?? index}>
                <TableCell>
                  {index > 0 ? (
                    <IconButton size="small" onClick={() => handleMoveUp(index)}>
                      <ArrowUpIcon fontSize="small" />
                    </IconButton>
                  ) : null}
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    value={field.label || ''}
                    onChange={(e) => handleFieldChange(index, { label: e.target.value })}
                    fullWidth
                  />
                </TableCell>
                <TableCell>
                  <FormControl size="small" fullWidth>
                    <Select
                      value={field.type || ''}
                      onChange={(e) => handleFieldChange(index, { type: e.target.value })}
                    >
                      {options.columnType?.map((type) => (
                        <MenuItem key={type.key} value={type.key}>
                          {type.value}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </TableCell>
                <TableCell>
                  <IconButton size="small" onClick={() => handleDeleteField(index)} color="error">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Box sx={{ p: 1 }}>
          <Button size="small" onClick={handleAddField}>
            Add Field
          </Button>
        </Box>
      </TableContainer>

      {/* Filename pattern + test UI (Knockout "Value from Test") disabled for now.
      <TextField label="Filename pattern" ... />
      <TextField label="Test with filename" ... />
      */}
    </Box>
  );
};
